import { ComboboxData } from "@mantine/core";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  SiteIntegration,
  SiteIntegrationConfiguration,
  SiteIntegrationGoogleAnalytics,
  SiteIntegrationGoogleSearchConsole,
  User,
  UserIdentity,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import FormLabel from "~/components/login/formLabel/FormLabel";
import Select from "~/components/mantine/select/Select";
import { Option, Select as UISelect } from "~/components/ui/select/Select";
import { useAuth } from "~/contexts/AuthProvider.tsx";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch";
import { QUERY_KEYS } from "~/pages/GoogleDashboardPage/constants";

import { GoogleAccountHandle } from "../GoogleAccountHandle";
import styles from "./IntegrationConfigurationPanel.module.scss";

type Property =
  | SiteIntegrationGoogleAnalytics
  | SiteIntegrationGoogleSearchConsole;

type FormValues = Partial<{
  identityId: string;
  property: Property;
}>;

const filterIdentities = (
  identities: UserIdentity[],
  user: User,
  integration: SiteIntegration,
) => {
  const selectedIdentity = identities.find(
    (identity) => identity.id === integration.user_identity_id,
  );

  const selectableIdentities = identities.filter(
    (identity) => identity.user_id === user.id,
  );

  if (
    selectedIdentity &&
    !selectableIdentities.some(
      (identity) => identity.id === selectedIdentity.id,
    )
  ) {
    selectableIdentities.unshift(selectedIdentity);
  }

  return selectableIdentities;
};

export const IntegrationConfigurationPanel = ({
  siteId,
  integration,
  identities,
}: {
  siteId: number;
  integration: SiteIntegration;
  identities: UserIdentity[];
}) => {
  const { user } = useAuth();

  const queryClient = useQueryClient();

  const { data: configurationData } = useSuspenseQuery({
    queryKey: QUERY_KEYS.INTEGRATION_CONFIGURATION(integration.id),
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.sites.integrationsConfigurationDetail(siteId, integration.id),
      );

      if (result.error) {
        return {
          error: result.error,
          accounts: [] as SiteIntegrationConfiguration,
        };
      }

      return {
        error: undefined,
        accounts: result.data.data.site_integration_configuration,
      };
    },
  });

  const { mutateAsync: updateIntegration } = useMutation({
    mutationFn: async (data: FormValues) => {
      await apiClient.sites.integrationsPartialUpdate(siteId, integration.id, {
        user_identity_id: parseInt(data.identityId ?? ""),
        properties: data.property ? [data.property] : [],
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.SITE_INTEGRATIONS(siteId),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.INTEGRATION_CONFIGURATION(integration.id),
        }),
      ]);

      return;
    },
  });

  const { control, watch, reset, handleSubmit, setValue, formState } =
    useForm<FormValues>({
      defaultValues: {
        identityId: integration.user_identity_id.toString(),
        property: integration.properties[0],
      },
      mode: "onChange",
    });

  const watchedValues = watch();

  useEffect(() => {
    const onSubmit = async (data: FormValues) => {
      const result = await tryCatch(updateIntegration(data));

      if (result.error) {
        enqueueSnackbar(messageFromError(result.error), {
          variant: "error",
          persist: true,
        });

        return;
      }

      reset(data);
    };

    if (
      watchedValues.identityId !== integration.user_identity_id.toString() ||
      watchedValues.property?.id !== integration.properties[0]?.id
    ) {
      handleSubmit(onSubmit)();
    }
  }, [
    watchedValues.identityId,
    watchedValues.property,
    handleSubmit,
    integration.user_identity_id,
    integration.properties,
    updateIntegration,
    reset,
  ]);

  const findPropertyById = (id: string) => {
    const properties = configurationData.accounts.flatMap(
      (account) => account.properties,
    );

    return properties?.find((property) => property.id === id);
  };

  const options: ComboboxData = [
    {
      value: "(empty)",
      label: "",
      disabled: true,
    },
    ...configurationData.accounts.map((account) => ({
      group: account.name,
      items: account.properties.map((property) => ({
        value: property.id,
        label: property.name,
      })),
    })),
  ];

  if (!user) return null;

  const selectableIdentities = filterIdentities(identities, user, integration);

  return (
    <div>
      <div className={styles.formRow}>
        <FormLabel label="Google Account" />
        <Controller
          control={control}
          name="identityId"
          render={({ field }) => (
            <UISelect
              className={styles.identitySelect}
              value={field.value}
              onChange={(_evt, value) => {
                field.onChange(value);
                if (value !== integration.user_identity_id.toString()) {
                  setValue("property", undefined);
                }
              }}
              disabled={formState.isSubmitting}
              renderValue={(option) => {
                if (!option || Array.isArray(option)) return null;

                const selectedIdentity = selectableIdentities.find(
                  (identity) => identity.id.toString() === option.value,
                );
                if (!selectedIdentity) return null;

                return (
                  <GoogleAccountHandle
                    identity={selectedIdentity}
                    variant="full"
                  />
                );
              }}
            >
              {selectableIdentities.map((identity) => (
                <Option key={identity.id} value={identity.id.toString()}>
                  <GoogleAccountHandle identity={identity} variant="full" />
                </Option>
              ))}
            </UISelect>
          )}
        />
      </div>

      <div className={styles.formRow}>
        <FormLabel label="Select property to pull data from:" />
        <Controller
          control={control}
          name="property"
          render={({ field }) => (
            <Select
              data={options}
              searchable={false}
              disabled={formState.isSubmitting}
              {...field}
              value={field.value?.id || "(empty)"}
              onChange={(_evt, selected) => {
                if (selected.value === "(empty)") {
                  field.onChange(undefined);

                  return;
                }

                field.onChange(findPropertyById(selected.value));
              }}
            />
          )}
        />
        {(() => {
          if (configurationData.error) {
            return (
              <p className={styles.errorHint}>
                {messageFromError(configurationData.error)}
              </p>
            );
          }

          if (!watchedValues.property) {
            return (
              <p className={styles.warningHint}>
                <Icon
                  iconName="WarningIcon"
                  width="1rem"
                  height="1rem"
                  className={styles.icon}
                />
                If you leave this blank, your integration will be effectively
                inactive.
              </p>
            );
          }

          return null;
        })()}
      </div>
    </div>
  );
};
